import { Box, Table, Title } from "@mantine/core";
import Decimal from "decimal.js";
import type { JSX } from "react";
import { useQuery } from "urql";
import Loading from "../Loading";
import classes from "./ApiCosts.module.css";
import { type ApiCostsDailyQuery, type ApiCostsDailyQueryVariables, apiCostsDailyQuery } from "./queries";
import { type DaySel, externalFilter, formatDay, formatMonth, type MonthSel } from "./utils";

interface DailyCostsProps {
    selectedModel: string | null;
    selectedExternal: string | null;
    selectedMonth: MonthSel;
    selectedDay: DaySel | null;
    onDaySelect: (sel: DaySel) => void;
}

function DailyCosts({
    selectedModel,
    selectedExternal,
    selectedMonth,
    selectedDay,
    onDaySelect,
}: DailyCostsProps): JSX.Element {
    const y = selectedMonth.year;
    const m = selectedMonth.month;
    const { externalId, noExternal } = externalFilter(selectedExternal);

    const [res] = useQuery<ApiCostsDailyQuery, ApiCostsDailyQueryVariables>({
        query: apiCostsDailyQuery,
        variables: { year: y, month: m, model: selectedModel || null, externalId, noExternal },
        requestPolicy: "cache-and-network",
    });

    const dailyCosts = res.data?.apiCostsDaily || [];

    let main: JSX.Element | null = null;
    if (res.fetching) {
        main = <Loading />;
    } else if (res.error) {
        main = <Box>Error: {res.error.message}</Box>;
    } else {
        main = (
            <Table>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Day</Table.Th>
                        <Table.Th>Cost (USD)</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {dailyCosts.length === 0 ? (
                        <Table.Tr>
                            <Table.Td colSpan={2} ta="center">
                                No costs found for the selected month and filters.
                            </Table.Td>
                        </Table.Tr>
                    ) : (
                        dailyCosts.map((dc) => {
                            const isSelected =
                                selectedDay?.year === dc.year &&
                                selectedDay?.month === dc.month &&
                                selectedDay?.day === dc.day;
                            return (
                                <Table.Tr
                                    key={`${dc.year}-${dc.month}-${dc.day}`}
                                    className={`${classes.clickableRow} ${isSelected ? classes.selectedRow : ""}`}
                                    onClick={() => onDaySelect({ year: dc.year, month: dc.month, day: dc.day })}
                                >
                                    <Table.Td>{formatDay(dc.year, dc.month, dc.day)}</Table.Td>
                                    <Table.Td>${new Decimal(dc.cost).toFixed(6)}</Table.Td>
                                </Table.Tr>
                            );
                        })
                    )}
                </Table.Tbody>
            </Table>
        );
    }

    return (
        <>
            <Title order={4} mt="xl">
                Daily Costs for {formatMonth(selectedMonth.year, selectedMonth.month)}
            </Title>
            {main}
        </>
    );
}

export default DailyCosts;
